/**
 * Shared JSON-in-markdown folding utility.
 *
 * Many LLM-produced memos / briefs / debate transcripts embed raw JSON
 * (either fenced in ```json or as bare objects). This module converts
 * those blobs into humanised markdown bullets so they render cleanly
 * inside ReactMarkdown — no `{ "key": ... }` syntax visible to the
 * end user.
 *
 * Mirror of `_fold_orphan_json` in `backend/lib/committee/pdf.py`.
 * Keep both in sync — there are tests in
 * `frontend/src/__tests__/jsonFold.test.js`.
 */

const KEY_LABELS = {
  overall_risk_level: 'Overall risk level',
  points_of_agreement: 'Points of agreement',
  points_of_disagreement: 'Points of disagreement',
  cio_should_weigh: 'CIO should weigh',
  passes: 'Compliance passes',
  restricted_list_hit: 'Restricted list hit',
  mcap_floor_hit: 'Market-cap floor hit',
  sebi_category_hit: 'SEBI category flag',
  sector_cap_hit: 'Sector cap hit',
  position_size_hit: 'Position-size hit',
  mandatory_adjustments: 'Mandatory adjustments',
  reasoning: 'Reasoning',
  notes: 'Notes',
  side: 'Side',
  round: 'Round',
  claim_ids_attacked: 'Claims attacked',
  new_claims: 'New claims',
  claim_id: 'Claim ID',
  text: 'Argument',
  supporting_fact_ids: 'Supporting facts',
  direction: 'Direction',
  conviction: 'Conviction',
  size_pct_nav: 'Size (% NAV)',
  entry_hint: 'Entry hint',
  stop_pct: 'Stop (%)',
  target_pct: 'Target (%)',
  horizon_days: 'Horizon (days)',
  rationale: 'Rationale',
  one_line_thesis: 'Thesis',
  verdict: 'Verdict',
  view_changed_from_last_run: 'View changed',
  change_drivers: 'Change drivers',
  similar_situations_used: 'Similar situations',
  compliance_status: 'Compliance status',
  compliance_overrides: 'Compliance overrides',
  illiquidity_flag: 'Illiquidity flag',
  fno_aware: 'F&O-aware',
  sebi_category_flag: 'SEBI category flag',
  body_md: 'Body',
  memo_md: 'Memo',
  headline: 'Headline',
  key_facts: 'Key facts',
  numeric_value: 'Value',
  unit: 'Unit',
  fact_id: 'Fact ID',
};

function humanKey(k) {
  if (KEY_LABELS[k]) return KEY_LABELS[k];
  return String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderValue(value, depth = 0) {
  const indent = '  '.repeat(depth);
  if (value === null || value === undefined) return `${indent}_n/a_`;
  if (typeof value === 'boolean') return `${indent}**${value ? 'Yes' : 'No'}**`;
  if (typeof value === 'number') return `${indent}**${value}**`;
  if (typeof value === 'string') return `${indent}${value}`;
  if (Array.isArray(value)) {
    if (value.length === 0) return `${indent}_(empty)_`;
    return value.map((v) => {
      if (v && typeof v === 'object') {
        // Render object inside an array as an indented sub-block
        const inner = renderValue(v, depth + 1).replace(/^\s+/, '');
        return `${indent}- ${inner}`;
      }
      return `${indent}- ${String(v)}`;
    }).join('\n');
  }
  if (typeof value === 'object') {
    return Object.entries(value).map(([k, v]) => {
      const label = humanKey(k);
      if (v && typeof v === 'object') {
        return `${indent}**${label}:**\n${renderValue(v, depth + 1)}`;
      }
      const flat = v === null || v === undefined ? '_n/a_'
        : typeof v === 'boolean' ? (v ? 'Yes' : 'No')
        : String(v);
      return `${indent}**${label}:** ${flat}`;
    }).join('\n\n');
  }
  return `${indent}${String(value)}`;
}

export function jsonToMarkdown(parsed) {
  if (parsed === null || parsed === undefined) return '_n/a_';
  if (typeof parsed !== 'object') return String(parsed);
  if (Array.isArray(parsed)) return renderValue(parsed, 0);
  return Object.entries(parsed).map(([k, v]) => {
    const label = humanKey(k);
    if (v === null || v === undefined) return `**${label}:** _n/a_`;
    if (typeof v !== 'object') {
      const flat = typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v);
      return `**${label}:** ${flat}`;
    }
    if (Array.isArray(v)) {
      if (v.length === 0) return `**${label}:** _(empty)_`;
      const items = v.map((item) => {
        if (item && typeof item === 'object') {
          const inner = renderValue(item, 1).replace(/^\s+/, '');
          return `- ${inner}`;
        }
        return `- ${String(item)}`;
      }).join('\n');
      return `**${label}**\n${items}`;
    }
    return `**${label}**\n${renderValue(v, 0)}`;
  }).join('\n\n');
}

const KEY_HINT_RE = /"\s*[A-Za-z0-9_]+\s*"\s*:/;

/**
 * Walk text from `start` and return the smallest balanced JSON
 * object/array string starting there. Honours quoted-string state so
 * braces inside strings don't unbalance the count.
 */
function balancedJsonAt(text, start) {
  if (start >= text.length) return null;
  const open = text[start];
  if (open !== '{' && open !== '[') return null;
  const close = open === '{' ? '}' : ']';
  let depth = 0;
  let inStr = false;
  let escape = false;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (inStr) {
      if (escape) { escape = false; continue; }
      if (c === '\\') { escape = true; continue; }
      if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') { inStr = true; continue; }
    if (c === open) depth += 1;
    else if (c === close) {
      depth -= 1;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }
  return null;
}

function foldInline(text) {
  if (!text) return text;
  const out = [];
  let i = 0;
  const n = text.length;
  while (i < n) {
    const ch = text[i];
    if (ch === '{' || ch === '[') {
      const blob = balancedJsonAt(text, i);
      if (blob && KEY_HINT_RE.test(blob) && blob.length >= 12) {
        try {
          const parsed = JSON.parse(blob);
          out.push(jsonToMarkdown(parsed));
          i += blob.length;
          continue;
        } catch (_e) { /* fall through */ }
      }
    }
    out.push(ch);
    i += 1;
  }
  return out.join('');
}

/**
 * Replace JSON blobs (fenced, multi-line, single-line, or unclosed/truncated)
 * inside a markdown body with humanised bullet lists.
 */
export function foldJsonInMarkdown(text) {
  if (!text || typeof text !== 'string') return text;

  // Pass 1: closed ``` fences. Tolerant of single-line bodies.
  const fenceRe = /```(?:json|JSON)?\s*([\s\S]*?)\s*```/g;
  let out = text.replace(fenceRe, (match, body) => {
    const trimmed = (body || '').trim();
    try {
      return `\n\n${jsonToMarkdown(JSON.parse(trimmed))}\n\n`;
    } catch (_e) {
      const folded = foldInline(trimmed);
      if (folded !== trimmed) return `\n\n${folded}\n\n`;
      return match;
    }
  });

  // Pass 2: unclosed fence (truncated content). Only one possible per doc;
  // detect by parity of ``` markers.
  const fenceCount = (out.match(/```/g) || []).length;
  if (fenceCount % 2 === 1) {
    out = out.replace(/```(?:json|JSON)?\s*([\s\S]*)$/, (_m, body) => {
      const rendered = [];
      let i = 0;
      const src = (body || '');
      while (i < src.length) {
        if (src[i] === '{' || src[i] === '[') {
          const blob = balancedJsonAt(src, i);
          if (blob && KEY_HINT_RE.test(blob)) {
            try {
              rendered.push(jsonToMarkdown(JSON.parse(blob)));
              i += blob.length;
              continue;
            } catch (_e) { /* fall through */ }
          }
        }
        i += 1;
      }
      if (rendered.length) {
        return `\n\n${rendered.join('\n\n')}\n\n_(truncated)_\n\n`;
      }
      return '\n\n_(truncated structured output)_\n\n';
    });
  }

  // Pass 3: bare inline JSON in any non-fenced segment.
  const parts = out.split(/(```[\s\S]*?```)/g);
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) parts[i] = foldInline(parts[i]);
  }
  return parts.join('');
}

/**
 * Turn `**Bare Bold Lines**` (which LLMs use as section titles instead of
 * `### Bare Bold Lines`) into proper level-3 headings so prose styling
 * gives them margin/weight.
 */
export function promoteBoldLinesToHeadings(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(
    /^\s*\*\*([^*\n]{2,80})\*\*\s*:?\s*$/gm,
    (_m, label) => `### ${label.trim()}`,
  );
}

export default { foldJsonInMarkdown, promoteBoldLinesToHeadings, jsonToMarkdown };
